import { Component, ViewChild, ElementRef, AfterViewInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import * as $ from 'jquery';
import { Utils } from '../../../service/utils';
import { Constants } from '../../../service/constants';
import { CacheService } from 'ionic-cache';
import { CommonService } from '../../../service/common.service';
import { Message } from '../../../service/dialog/message.interface';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MachineOptions, MachineAdjustments, MachineBrandLevel } from 'src/service/formulas/objects';

@Component({
  selector: 'section-machine',
  templateUrl: 'machine.html',
  styleUrls: ['machine.scss']
})
export class Machine implements AfterViewInit {
  private isApp: boolean;
  public resetHoldingActive: boolean = false;
  private subscription: Subscription;
  public windowsDimensions: object = undefined;
  public machineFormGroup: FormGroup;
  public someRange: number = 0;
  public spindles: object[];
  public mapMode: number = undefined;
  public holders: object[];
  public popupVisible: object;
  public machineOptions: { [productKey: number]: MachineBrandLevel };
  public filteredSpindles: MachineOptions[];
  public brandKey: number = undefined;
  public holderAdjustment: MachineAdjustments[];
  public spindleAdjustment: MachineAdjustments[];
  public filteredHolders: MachineOptions[];
  public someKeyboardConfig: any = {
    connect: [true, false],
    start: 100,
    keyboard: true,
    step: 1,
    pageSteps: 10,
    range: {
      min: 75,
      max: 125
    }
  };

  //@ViewChild('nouislider') nouislider: ElementRef;
  @ViewChild('maxRPM', { static: true }) maxRPM: ElementRef;
  @ViewChild('maxIPM', { static: true }) maxIPM: ElementRef;
  @ViewChild('workHoldingSecurity', { static: true }) workHoldingSecurity: ElementRef;
  @Output() machineChange: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    public utils: Utils,
    public constants: Constants,
    private formBuilder: FormBuilder,
    private cache: CacheService,
    private commonService: CommonService
  ) {
    this.isApp = this.utils.isApp();

    if (this.isApp) {
      this.cache.getItem('windowsDimensions').then(windowsDimensions => {
        this.windowsDimensions = windowsDimensions;
      });
    }

    this.machineFormGroup = this.formBuilder.group({
      spindleSelect: new FormControl('CV40'),
      holderSelect: new FormControl(''),
      maxRPM: new FormControl(null, [Validators.pattern(this.constants.NUMBER_ONLY_REGEX)]),
      maxIPM: new FormControl(null, [Validators.pattern(this.constants.NUMBER_ONLY_REGEX)]),
      withHolding: new FormControl({ value: 100, disabled: false })
    });

    this.popupVisible = {
      spindleSelect: false,
      holderSelect: false
    };

    this.cache
      .getItem('machine')
      .then(data => {
        this.machineOptions = { ...data };
      })
      .catch(err => {
        console.log(err);
      });
  }

  getHolderAndSpindleOptions = (brandKey, mapMode) => {
    if (!!this.machineOptions[brandKey] && !!this.machineOptions[brandKey].mapModes[mapMode]) {
      this.filteredHolders = this.machineOptions[brandKey].mapModes[mapMode].holders;
      this.filteredSpindles = this.machineOptions[brandKey].mapModes[mapMode].spindles;

      const selectedHolder = this.filteredHolders.find(h => h.name === this.machineFormGroup.get('holderSelect').value);
      const selectedSpindle = this.filteredSpindles.find(
        s => s.name === this.machineFormGroup.get('spindleSelect').value
      );

      if (!!selectedHolder) {
        this.holderAdjustment = selectedHolder.adjustments;
      } else {
        this.holderAdjustment = null;
        this.machineFormGroup.get('holderSelect').patchValue('');
      }
      if (!!selectedSpindle) {
        this.spindleAdjustment = selectedSpindle.adjustments;
      } else {
        this.spindleAdjustment = null;
        this.machineFormGroup.get('spindleSelect').patchValue('');
      }
    } else {
      this.holderAdjustment = null;
      this.machineFormGroup.get('holderSelect').patchValue('');
      this.spindleAdjustment = null;
      this.machineFormGroup.get('spindleSelect').patchValue('');
      let message: Message = {
        title: 'Wrong combination',
        message: 'There are no Spindles or Holders options for this tool/toolpath combination'
      };
      this.utils.openDialog(message, 'error');
    }
    this.emitData('');
  };

  resetWithHolding = (emit: boolean = true) => {
    this.machineFormGroup.get('withHolding').patchValue(100, { emitEvent: emit });
    this.resetHoldingActive = false;
    this.machineFormGroup.get('withHolding').enable();
  };

  setMachine = (control: string) => {
    this.popupVisible[control] = false;
    this.emitData('setMachine');
    if (this.isApp) {
      if (control === 'holderSelect') {
        this.utils.apptentiveEngage('Holder');
      } else {
        this.utils.apptentiveEngage('Spindle');
      }
    }
  };

  setValue = (event: MatOptionSelectionChange, value?: MachineOptions) => {
    if (event.isUserInput) {
      if (value.type === 'Holder') {
        this.holderAdjustment = value.adjustments;
      }
      if (value.type === 'Spindle') {
        this.spindleAdjustment = value.adjustments;
      }
      setTimeout(() => {
        this.commonService.notifyOther({
          option: 'recommendationsGeneratedForLoggingData',
          value: {
            logLocation: 'machine change'
          }
        });
      });
    }
  };

  emitData = (fromWhere: string = '') => {
    this.machineChange.emit({
      spindle: this.machineFormGroup.get('spindleSelect').value,
      holder: this.machineFormGroup.get('holderSelect').value,
      fixtureBar: this.machineFormGroup.get('withHolding').value ? this.machineFormGroup.get('withHolding').value : 100,
      maxRPM: this.machineFormGroup.get('maxRPM').value,
      maxIPM: this.machineFormGroup.get('maxIPM').value,
      holderAdjustment: this.holderAdjustment,
      spindleAdjustment: this.spindleAdjustment,
      dirty: false
    });
  };

  ngAfterViewInit() {
    this.subscription = this.commonService.notifyObservable$.subscribe(res => {
      if (res.hasOwnProperty('option') && res.option === 'logout') {
        this.subscription.unsubscribe();
      }
      if (res.hasOwnProperty('option') && res.option === 'reset') {
        this.machineFormGroup.reset({}, { emitEvent: false });
        this.machineFormGroup.get('spindleSelect').patchValue('CV40', { emitEvent: false });
        this.resetWithHolding(false);

        let maxRPMLabel = $(this.maxRPM.nativeElement)
          .parents('.mat-form-field-infix')
          .find('.mat-form-field-label-wrapper')
          .find('.mat-form-field-label');
        if (!maxRPMLabel.hasClass('mat-empty') && !maxRPMLabel.hasClass('mat-form-field-empty')) {
          maxRPMLabel.addClass('mat-empty').addClass('mat-form-field-empty');
        }

        let maxIPMLabel = $(this.maxIPM.nativeElement)
          .parents('.mat-form-field-infix')
          .find('.mat-form-field-label-wrapper')
          .find('.mat-form-field-label');
        if (!maxIPMLabel.hasClass('mat-empty') && !maxIPMLabel.hasClass('mat-form-field-empty')) {
          maxIPMLabel.addClass('mat-empty').addClass('mat-form-field-empty');
        }
      }
      if ((res.hasOwnProperty('option') && res.option === 'mapModeChanged') || res.option === 'clearToolPath') {
        if (res.value) {
          this.mapMode = res.value.mapMode;
          if (this.mapMode === 2) {
            this.machineFormGroup.get('withHolding').patchValue(100, { emitEvent: true });
            $(this.workHoldingSecurity.nativeElement)
              .parents('.workHoldingSecurityContainer')
              .find('.shadow')
              .addClass('disabled');
            this.machineFormGroup.get('withHolding').disable();
          } else {
            this.machineFormGroup.get('withHolding').enable();
            $(this.workHoldingSecurity.nativeElement)
              .parents('.workHoldingSecurityContainer')
              .find('.shadow')
              .removeClass('disabled');
          }
        }
      }
      if (res.hasOwnProperty('option') && res.option === 'machine') {
        this.brandKey = res.value.tool ? res.value.tool.brandKey : null;
        if (this.brandKey && this.mapMode) {
          this.getHolderAndSpindleOptions(this.brandKey, this.mapMode);
        }
      }
      if (res.hasOwnProperty('option') && res.option === 'resetWorkHoldingSecurity') {
        this.resetWithHolding(false);
      }
    });

    this.machineFormGroup
      .get('withHolding')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((value: number) => {
        if (value !== 100) {
          this.resetHoldingActive = true;
        } else {
          this.resetHoldingActive = false;
        }
        this.emitData('withHolding');
      });

    this.machineFormGroup
      .get('maxRPM')
      .valueChanges.pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value: string) => {
        this.emitData('maxRPM');
        if (this.isApp) {
          this.utils.apptentiveEngage('Max RPM');
        }
      });

    this.machineFormGroup
      .get('maxIPM')
      .valueChanges.pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value: string) => {
        this.emitData('maxIPM');
        if (this.isApp) {
          this.utils.apptentiveEngage('Max IPM');
        }
      });
  }

  onEnter = control => {
    if (this.machineFormGroup.get(control).value !== '') {
      this.popupVisible[control] = false;
    } else {
      this.popupVisible[control] = true;
    }
  };

  onLeave = control => {
    this.popupVisible[control] = false;
  };

  onKeyUp = control => {
    if (this.machineFormGroup.get(control).value !== '') {
      this.popupVisible[control] = false;
    } else {
      this.popupVisible[control] = true;
    }
  };
}
